import { Box, Paper, Slider, Typography, Button, Chip, useTheme } from "@mui/material";
import { FiPlus, FiRefreshCw, FiBarChart2, FiChevronRight, FiLayers, FiActivity, FiBriefcase, FiShield } from "react-icons/fi";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../components/button/RefreshButton";
import PivotableBarChart from "../../components/chart/PivotableBarChart";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import ResizableBox from "../../components/container/ResizableBox";
import StandardCardContainer from "../../components/container/StandardCardContainer";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import AgGrid from "../../components/grid/AgGrid";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef, translationFormater } from "../../components/grid/columnDefinitions/columnDefinitions";
import CheckBox from "../../components/input/CheckBox";
import SelectBox from "../../components/input/SelectBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import { ReportStatus } from "./ReportTypes";
import "../../style/pages-style/Engagement/engagement.css";
import MetricCard from "../../app_components/MetricCard";
import SearchBox from "../../components/input/SearchBox";
import { TableFooterContainer } from "../../components/container/TableFooterContainer";
import { GridPagination } from "../../components/grid/AgGrid";

const Supplier_Directory_My_Submissions = () => {
  const { t } = useTranslation();
  const table_1_ref = useRef();
  const table_2_ref = useRef();
  const { columnVisibilities, refresh, setRefresh } = useContextProvider();
  const emission = useMemo(() => ({ label: "Emission", value: "Emission", company_field: "gwp", report_field: "gwp_per_unit" }), []);
  const cost = useMemo(() => ({ label: "Cost", value: "Cost", company_field: "cost", report_field: "cost_per_unit" }), []);
  const score = useMemo(() => ({ label: "Score", value: "Score", company_field: "score", report_field: "score" }), []);
  const comparisonOptions = useMemo(() => [emission, cost, score], [emission, cost, score]);
  const [comparison, setComparison] = useState(emission.value);
  const [reports, setReports] = useState([]);
  const [acceptedReports, setAcceptedReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [optionCompanies, setOptionCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [optionYears, setOptionYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [emissionWeight, setEmissionWeight] = useState(50);
  const [status, setStatus] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showColVisibilityMenu_1, setShowColVisibilityMenu_1] = useState(false);
  const [showColVisibilityMenu_2, setShowColVisibilityMenu_2] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [showGraph_2, setShowGraph_2] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [searchText_1, setSearchText_1] = useState("");
  const [searchText_2, setSearchText_2] = useState("");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const colVisibility_key = "supplier_directory_my_submissions_company_table";
  const colVisibility_key_2 = "supplier_directory_my_submissions_report_table";
  const isHidden = useCallback((field) => !columnVisibilities[colVisibility_key]?.includes(field), [columnVisibilities, colVisibility_key]);
  const isHidden_2 = useCallback((field) => !columnVisibilities[colVisibility_key_2]?.includes(field), [columnVisibilities, colVisibility_key_2]);
  const costWeight = 100 - emissionWeight;
  const colDefs = useMemo(
    () => [
      { field: "name", headerName: t("Name"), ...leftAlignColDef, ...defaultColDef },
      { field: "cost", headerName: t("Cost"), ...rightAlignColDef, ...defaultColDef },
      { field: "gwp", headerName: t("GWP"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "score", headerName: t("Score"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    ],
    [t]
  );
  const colDefs_2 = useMemo(
    () => [
      { field: "name", headerName: t("Name"), ...leftAlignColDef, ...defaultColDef },
      { field: "year", headerName: t("Year"), ...centerAlignColDef, ...defaultColDef },
      { field: "quantity", headerName: t("Quantity"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "cost", headerName: t("Cost"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "cost_per_unit", headerName: t("Cost Per Unit"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "gwp", headerName: t("GWP"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "gwp_per_unit", headerName: t("GWP Per Unit"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "score", headerName: t("Score"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "functional_unit", headerName: t("Functional Unit"), ...centerAlignColDef, ...defaultColDef },
      { field: "other_unit", headerName: t("Other Unit"), ...centerAlignColDef, ...defaultColDef },
      { field: "level", headerName: t("Level"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
      { field: "scope", headerName: t("Scope"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
      { field: "status", headerName: t("Status"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
      { field: "last_modified", headerName: t("Last Modified"), ...rightAlignColDef, ...defaultColDef },
      { field: "created_at", headerName: t("Created At"), ...rightAlignColDef, ...defaultColDef },
    ],
    [t]
  );
  const columnDefs = useMemo(() => colDefs.map((col) => ({ ...col, hide: isHidden(col.field) })), [colDefs, isHidden]);
  const columnDefs_2 = useMemo(() => colDefs_2.map((col) => ({ ...col, hide: isHidden_2(col.field) })), [colDefs_2, isHidden_2]);
  const graphs = [
    { title: "GWP", countBy: "gwp" },
    { title: "Cost", countBy: "cost" },
    { title: "Score", countBy: "score" },
  ];
  const graphs_2 = [
    { title: "GWP", countBy: "gwp" },
    { title: "Cost", countBy: "cost" },
    { title: "Score", countBy: "score" },
    { title: "GWP Per Unit", countBy: "gwp_per_unit" },
    { title: "Cost Per Unit", countBy: "cost_per_unit" },
  ];
  useEffect(() => {
    let temp = [...reports];
    if (selectedCompany) {
      temp = temp.filter((r) => r?.receiver_id === selectedCompany);
    }
    if (selectedYear) {
      temp = temp.filter((r) => r?.year === selectedYear);
    }
    if (status) {
      temp = temp.filter((r) => r?.status === status);
    }
    setAcceptedReports(temp);
  }, [reports, selectedCompany, selectedYear, status]);
  useEffect(() => {
    Supplier_Reports.get_mine_sent()
      .then((res) => res.json())
      .then((data) => setReports(data));
  }, [refresh]);
  useEffect(() => {
    const uniqueCompanies = reports.reduce((acc, report) => {
      if (!acc.some((c) => c.value === report.receiver_id)) {
        acc.push({ label: report.receiver_name, value: report.receiver_id });
      }
      return acc;
    }, []);

    setOptionCompanies(uniqueCompanies);
  }, [reports]);
  useEffect(() => {
    setOptionYears(
      reports.reduce((acc, report) => {
        if (!acc.some((c) => c.value === report.year)) {
          acc.push({ label: String(report.year), value: report.year });
        }
        return acc;
      }, [])
    );
  }, [reports]);
  useEffect(() => {
    const temp = new Map();
    const getScore = (r) => (emissionWeight * r.gwp + costWeight * r.cost) / 100;
    acceptedReports.forEach((report) => {
      const score = getScore(report);
      if (temp.has(report.receiver_id)) {
        const existing = temp.get(report.receiver_id);
        existing.gwp += report.gwp || 0;
        existing.cost += report.cost || 0;
        existing.reports.push({ ...report, score });
        existing.score += score || 0;
      } else {
        temp.set(report.receiver_id, {
          name: report.receiver_name,
          id: report.receiver_id,
          gwp: report.gwp || 0,
          cost: report.cost || 0,
          reports: [{ ...report, score }],
          score,
        });
      }
    });
    let arr = Array.from(temp.values());
    if (comparison) {
      const co = comparisonOptions.find((c) => c.value === comparison);
      arr = arr.sort((a, b) => a[co.company_field] > b[co.company_field]);
      arr.forEach((item) => {
        if (Array.isArray(item.reports)) {
          item.reports = item.reports.sort((r1, r2) => r1[co.report_field] - r2[co.report_field]);
        }
      });
    }

    setCompanies(arr);
  }, [acceptedReports, comparison, emissionWeight, costWeight, comparisonOptions]);
  useEffect(() => {
    setSelectedReports(companies.find((c) => c.id === selectedCompanies[0])?.reports || []);
  }, [companies, selectedCompanies]);

  useEffect(() => {
    if (table_1_ref.current?.api) {
      table_1_ref.current.api.setGridOption("quickFilterText", searchText_1);
    }
  }, [searchText_1]);

  useEffect(() => {
    if (table_2_ref.current?.api) {
      table_2_ref.current.api.setGridOption("quickFilterText", searchText_2);
    }
  }, [searchText_2]);

  // Aggregated Metrics for KPI Cards
  const totalSubmissions = useMemo(() => reports.length, [reports]);
  const avgDataFidelity = 100; // Placeholder for logic
  const topMetricValue = useMemo(() => {
    if (comparison === emission.value) {
      const totalKg = reports.reduce((sum, r) => sum + (r.gwp || 0), 0);
      return `${(totalKg / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`;
    }
    if (comparison === cost.value) return `$${reports.reduce((sum, r) => sum + (r.cost || 0), 0).toLocaleString()}`;
    return "N/A";
  }, [reports, comparison, emission, cost]);

  const [activeTab, setActiveTab] = useState('directory'); // 'directory', 'analytics', 'intelligence'

  return (
    <MainContainer>
      {/* ── Standardized High-Fidelity KPI Grid ────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 3, mb: 5 }}>
        <MetricCard
          title={t("Active Submissions")}
          subtitle={t("Incoming data links")}
          value={totalSubmissions}
          icon={FiLayers}
          accent="#3b82f6"
          bg="rgba(59, 130, 246, 0.08)"
          delay={0.1}
        />
        <MetricCard
          title={t("Verified Suppliers")}
          subtitle={t("Unique partners on-boarded")}
          value={optionCompanies.length}
          icon={FiBriefcase}
          accent="#7c3aed"
          bg="rgba(124, 58, 237, 0.08)"
          delay={0.2}
        />
        <MetricCard
          title={t("Total Inbound Impact")}
          subtitle={comparison === emission.value ? t("Inbound Carbon") : t("Portfolio Value")}
          value={reports.reduce((sum, r) => sum + (comparison === emission.value ? (r.gwp || 0) : (r.cost || 0)), 0)}
          unit={comparison === emission.value ? t("kg CO₂e") : "₹"}
          icon={FiActivity}
          accent="#f59e0b"
          bg="rgba(245, 158, 11, 0.08)"
          delay={0.3}
        />
        <MetricCard
          title={t("Audit Fidelity")}
          subtitle={t("Data verification status")}
          value={avgDataFidelity}
          unit="%"
          icon={FiShield}
          accent="#10b981"
          bg="rgba(16, 185, 129, 0.08)"
          delay={0.4}
        />
      </Box>

      {/* ── Focused Tab Navigation ─────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
        <Box className="org-tab-container">
          <Box
            className={`org-tab-item ${activeTab === 'directory' ? "active" : ""}`}
            onClick={() => setActiveTab('directory')}
          >
            <FiLayers size={14} />
            {t("Submission Directory")}
          </Box>
          <Box
            className={`org-tab-item ${activeTab === 'analytics' ? "active" : ""}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FiBarChart2 size={14} />
            {t("Analysis Portal")}
          </Box>
          {selectedCompanies.length === 1 && (
            <Box
              className={`org-tab-item ${activeTab === 'intelligence' ? "active" : ""}`}
              onClick={() => setActiveTab('intelligence')}
            >
              <FiChevronRight size={14} />
              {t("Entity Intelligence")}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Dynamic Workspace View ─────────────────────────────── */}
      <Box sx={{ position: "relative", minHeight: "600px" }}>

        {/* PILLAR 1: Directory & Multi-dimensional Filters */}
        {activeTab === 'directory' && (
          <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
            <Paper variant="Table Container" sx={{ borderRadius: ORG_PALETTE.radius.card, overflow: "hidden" }}>
              <TableHeaderContainer>
                <TableTitleContainer>
                  <Typography variant="Table Title">{t("Partner Submission Registry")}</Typography>
                </TableTitleContainer>
                <TableHeaderToolsContainer ref={table_1_ref}>
                  <Box sx={{ width: 450 }}>
                    <SearchBox text={searchText_1} setText={setSearchText_1} placeholder={t("Search companies...")} width={450} />
                  </Box>
                  <ColumnVisibilityButton onClick={() => setShowColVisibilityMenu_1(true)} />
                  <RefreshButton setRefresh={setRefresh} />
                </TableHeaderToolsContainer>
              </TableHeaderContainer>
              <ResizableBox>
                <AgGrid suppressPagination={true} ref={table_1_ref} rowData={companies} columnDefs={columnDefs} onSelectionChanged={(e) => setSelectedCompanies(e.api.getSelectedRows().map(r => r.id))} />
              </ResizableBox>
              <TableFooterContainer sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, pb: 2 }}>
                <GridPagination gridRef={table_1_ref} />
              </TableFooterContainer>
            </Paper>
          </Box>
        )}

        {/* PILLAR 2: Portfolio Analytics */}
        {activeTab === 'analytics' && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 5, animation: "org-fadeInUp 0.4s ease" }}>
            <Box sx={{ mb: 1, textAlign: "left" }}>
              <Typography variant="Page Title" sx={{ mb: 0.5, color: tok.text.primary }}>
                {t("Portfolio Sustainability Analytics")}
              </Typography>
              <Typography variant="Page Subtitle" sx={{ color: tok.text.chartSub }}>
                {t("Strategic indexing of environmental footprint across your active client partnerships")}
              </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(600px, 1fr))", gap: 4 }}>
              {graphs.map((g, i) => (
                <StandardCardContainer key={i} sx={{ border: "1px solid", borderColor: tok.border.card, borderRadius: ORG_PALETTE.radius.card, overflow: "hidden" }}>
                  <TableHeaderContainer>
                    <TableTitleContainer>
                      <Typography variant="Table Title">
                        {t("Total")} {t(g.title)} {t("by Supplier")}
                      </Typography>
                    </TableTitleContainer>
                  </TableHeaderContainer>
                  <Box sx={{ p: 2, height: 400 }}>
                    <PivotableBarChart
                      usages={companies}
                      groupBy={"name"}
                      countBy={g.countBy}
                      xAxisLabel={t(g.title)}
                      yAxisLabel={t("Supplier")}
                    />
                  </Box>
                </StandardCardContainer>
              ))}
            </Box>
          </Box>
        )}

        {/* PILLAR 3: Granular Entity Intelligence */}
        {activeTab === 'intelligence' && selectedReports.length > 0 && (
          <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
            <Box sx={{
              mb: 4, p: 4,
              background: "#ffffff",
              borderRadius: "24px",
              border: "1px solid rgba(0,0,0,0.06)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.02)"
            }}>
              <Box>
                <Typography variant="Page Title" sx={{ mb: 0.5, color: tok.text.primary }}>
                  {companies.find(c => c.id === selectedCompanies[0])?.name}
                </Typography>
                <Typography variant="Page Subtitle" sx={{ color: tok.text.chartSub }}>
                  {t("Granular Supply Chain Data & Compliance Records")}
                </Typography>
              </Box>
              <Chip
                label={t("Partner Analytics")}
                sx={{
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? "rgba(101, 157, 51, 0.15)" : "rgba(101, 157, 51, 0.08)",
                  color: "#659d33",
                  fontWeight: 800,
                  fontSize: 12,
                  borderRadius: "8px",
                  height: 32
                }}
              />
            </Box>

            <Paper variant="Table Container" sx={{ borderRadius: ORG_PALETTE.radius.card, overflow: "hidden", mb: 4 }}>
              <TableHeaderContainer>
                <TableTitleContainer>
                  <Typography variant="Table Title">{t("Granular Submission Breakdown")}</Typography>
                </TableTitleContainer>
                <TableHeaderToolsContainer ref={table_2_ref}>
                  <Box sx={{ width: 450 }}>
                    <SearchBox text={searchText_2} setText={setSearchText_2} placeholder={t("Search line items...")} width={450} />
                  </Box>
                  <ColumnVisibilityButton onClick={() => setShowColVisibilityMenu_2(true)} />
                </TableHeaderToolsContainer>
              </TableHeaderContainer>
              <ResizableBox>
                <AgGrid suppressPagination={true} ref={table_2_ref} rowData={selectedReports} columnDefs={columnDefs_2} />
              </ResizableBox>
              <TableFooterContainer sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, pb: 2 }}>
                <GridPagination gridRef={table_2_ref} />
              </TableFooterContainer>
            </Paper>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(600px, 1fr))", gap: 3 }}>
              {graphs_2.map((g, i) => (
                <StandardCardContainer key={i} sx={{ border: "1px solid", borderColor: tok.border.card, borderRadius: ORG_PALETTE.radius.card, overflow: "hidden" }}>
                  <TableHeaderContainer>
                    <TableTitleContainer>
                      <Typography variant="Table Title">
                        {t("Itemized")} {t(g.title)} {t("Breakdown")}
                      </Typography>
                    </TableTitleContainer>
                  </TableHeaderContainer>
                  <Box sx={{ p: 2, height: 400 }}>
                    <PivotableBarChart
                      usages={selectedReports}
                      groupBy={"name"}
                      countBy={g.countBy}
                      xAxisLabel={t(g.title)}
                      yAxisLabel={t("Item Name")}
                    />
                  </Box>
                </StandardCardContainer>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <ColumnMenu anchorRef={table_1_ref} visible={showColVisibilityMenu_1} onClose={() => setShowColVisibilityMenu_1(false)} colDefs={colDefs} colVisibility_key={colVisibility_key} defaultCols={["name"]} />
      <ColumnMenu anchorRef={table_2_ref} visible={showColVisibilityMenu_2} onClose={() => setShowColVisibilityMenu_2(false)} colDefs={colDefs_2} colVisibility_key={colVisibility_key_2} defaultCols={["name"]} />
    </MainContainer>
  );
};
export default Supplier_Directory_My_Submissions;
